/**
 * Retención del IGV en la emisión de comprobantes (régimen de retenciones, R.S.
 * 037-2002/SUNAT y R.S. 033-2014/SUNAT).
 *
 * Reglas que la vendedora no puede saltear:
 *   1. la tasa es el 3% del importe de la operación; en pagos parciales, de cada pago.
 *      Por eso la factura final que deduce un anticipo retiene sobre el saldo a pagar y
 *      no sobre la operación completa: sumar las dos retenciones da el 3% del total;
 *   2. no se retiene si la operación no supera S/ 700: *"Se exceptúa de la obligación de
 *      retener cuando el pago efectuado es igual o inferior a S/ 700 y la suma del
 *      importe de la operación [...] no supera dicho importe"*. El umbral se mide sobre
 *      la OPERACIÓN: un anticipo de S/ 500 dentro de una venta de S/ 5,000 sí retiene;
 *   3. nunca junto con detracción (son excluyentes) y solo en Factura.
 *
 * La retención NO es un pago: el cliente la deposita en la cuenta de SUNAT (Banco de la
 * Nación) y la empresa la usa como crédito fiscal. Lo que entra a la cuenta de la
 * empresa es `total - retención`.
 */
import { parseMonto } from '@/utils/montos'

/** SUNAT: las operaciones de hasta S/ 700 no están sujetas a retención. */
export const UMBRAL_RETENCION_SOLES = 700

/** Tasa por defecto. El monto real lo calcula la sede con su configuración. */
export const TASA_RETENCION_PORCENTAJE = 3

/**
 * Motivo por el que la retención no puede aplicarse a esta emisión, o `null` si aplica.
 *
 * @param {object} opciones
 * @param {number} opciones.importeOperacionSoles importe de la operación completa en soles
 * @param {boolean} opciones.esFactura el comprobante es Factura (tipo 01)
 * @param {boolean} opciones.conDetraccion la misma operación va con detracción
 * @returns {string|null}
 */
export const motivoRetencionNoAplica = ({ importeOperacionSoles, esFactura, conDetraccion }) => {
    if (!esFactura) return 'La retención del IGV solo aplica a Factura.'
    if (conDetraccion) return 'La detracción y la retención no pueden aplicarse sobre la misma operación.'

    const importe = Number(importeOperacionSoles) || 0
    if (!(importe > UMBRAL_RETENCION_SOLES)) {
        return `La operación no supera S/ ${UMBRAL_RETENCION_SOLES.toFixed(2)}: por debajo de ese monto no corresponde retener.`
    }

    return null
}

/**
 * Detecta si el monto que se está cargando es el depósito neto y no el importe facturado.
 *
 * Es el error reportado: la vendedora copia lo que el cliente depositó (total − 3%) como
 * monto del anticipo, y el comprobante sale 3% corto. Con retención el depósito coincide
 * con el neto, así que esa coincidencia se avisa.
 *
 * @param {object} opciones
 * @param {number|string} opciones.montoIngresado monto cargado en el formulario
 * @param {number|string} opciones.deposito suma de los pagos de la venta seleccionados
 * @returns {boolean}
 */
export const esDepositoNeto = ({ montoIngresado, deposito }) => {
    const monto = parseMonto(montoIngresado)
    const neto = parseMonto(deposito)

    return monto > 0 && neto > 0 && Math.abs(monto - neto) < 0.01
}

/**
 * Retención y neto que el cliente deposita en la cuenta de la empresa, en la moneda del
 * comprobante. Con retención, el depósito es el importe facturado menos el 3%.
 *
 * @param {object} opciones
 * @param {number|string} opciones.importeComprobante importe facturado del comprobante
 * @param {number} [opciones.porcentaje] tasa de retención (3 por defecto)
 * @returns {{monto: number, neto: number}}
 */
export const calcularRetencion = ({ importeComprobante, porcentaje = TASA_RETENCION_PORCENTAJE }) => {
    const importe = parseMonto(importeComprobante)
    const monto = Number((importe * (porcentaje / 100)).toFixed(2))
    const neto = Number((importe - monto).toFixed(2))

    return { monto, neto }
}
